import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PendingAiSummaryEntry,
  PendingAiSummariesState,
  RemovePendingAiSummaryPayload,
} from './types';

// Pending summaries are transient runtime state - they live only until their
// threat's "created" WebSocket event lands (or until pruned). Excluded from
// redux-persist (see the whitelist in src/store/index.ts).
const MAX_AGE_MS = 15 * 60 * 1000;

const initialState: PendingAiSummariesState = {
  entries: [],
};

const pendingAiSummariesSlice = createSlice({
  name: 'pendingAiSummaries',
  initialState,
  reducers: {
    addPendingAiSummaries: (
      state,
      action: PayloadAction<PendingAiSummaryEntry[]>,
    ) => {
      const incoming = action.payload;
      if (incoming.length === 0) return;
      // Prune entries older than MAX_AGE relative to the newest incoming
      // timestamp so the list can't grow unbounded when a "created" event is
      // never received (analyst navigated away, missed WebSocket message).
      const newest = incoming.reduce(
        (max, e) => (e.createdAt > max ? e.createdAt : max),
        incoming[0].createdAt,
      );
      const cutoff = new Date(
        new Date(newest).getTime() - MAX_AGE_MS,
      ).toISOString();
      state.entries = state.entries
        .filter(e => e.createdAt >= cutoff)
        .concat(incoming);
    },
    removePendingAiSummary: (
      state,
      action: PayloadAction<RemovePendingAiSummaryPayload>,
    ) => {
      const { threatName, companyId } = action.payload;
      const idx = state.entries.findIndex(
        e => e.threatName === threatName && e.companyId === companyId,
      );
      if (idx !== -1) state.entries.splice(idx, 1);
    },
    clearPendingAiSummaries: state => {
      state.entries = [];
    },
  },
});

export const {
  addPendingAiSummaries,
  removePendingAiSummary,
  clearPendingAiSummaries,
} = pendingAiSummariesSlice.actions;
export default pendingAiSummariesSlice.reducer;
